import type { MenuItem } from 'primeng/api';
import type { ThemeMode } from './theme.service';

/** Ce dont les entrées ont besoin de {@link LanguageService} (type structurel : testable sans Transloco). */
export interface LanguagePreference {
  shortLabel(): string;
  cycle(): void;
}

/** Ce dont les entrées ont besoin de {@link ThemeService}. */
export interface ThemePreference {
  mode(): ThemeMode;
  cycle(): void;
}

/**
 * Une préférence affichée dans le menu du compte (avatar) : libellé à traduire, valeur courante
 * (texte brut ou clé), icône PrimeIcons, action. La langue et le thème ont quitté la barre du haut
 * pour ce menu ; le tiroir mobile garde ses propres pastilles.
 */
export interface PreferenceEntry {
  id: 'language' | 'theme';
  labelKey: string;
  /** Valeur affichée telle quelle (code langue : « FR », « EN »). */
  value?: string;
  /** Valeur à traduire (thème : « Clair », « Sombre », « Auto »). */
  valueKey?: string;
  icon: string;
  run(): void;
}

const THEME_SHORT_KEY: Record<ThemeMode, string> = {
  system: 'nav.themeShortSystem',
  light: 'nav.themeShortLight',
  dark: 'nav.themeShortDark',
};

const THEME_ICON: Record<ThemeMode, string> = {
  system: 'pi pi-desktop',
  light: 'pi pi-sun',
  dark: 'pi pi-moon',
};

/** Langue puis thème, avec leur valeur courante. Lire les signaux dans un `computed()` pour suivre. */
export function preferenceEntries(lang: LanguagePreference, theme: ThemePreference): PreferenceEntry[] {
  const mode = theme.mode();
  return [
    {
      id: 'language',
      labelKey: 'nav.language',
      value: lang.shortLabel(),
      icon: 'pi pi-globe',
      run: () => lang.cycle(),
    },
    {
      id: 'theme',
      labelKey: 'nav.theme',
      valueKey: THEME_SHORT_KEY[mode],
      icon: THEME_ICON[mode],
      run: () => theme.cycle(),
    },
  ];
}

/** Variante `p-menu` (intégrateur) : « Langue : FR », « Thème : Sombre ». */
export function preferencesMenuItems(
  translate: (key: string) => string,
  lang: LanguagePreference,
  theme: ThemePreference
): MenuItem[] {
  return preferenceEntries(lang, theme).map(e => ({
    id: `pref-${e.id}`,
    label: `${translate(e.labelKey)} : ${e.value ?? translate(e.valueKey ?? '')}`,
    icon: e.icon,
    command: () => e.run(),
  }));
}
